import React, { FC, useEffect } from 'react';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';

import { Currency, Icon } from '@column/column-ui-kit';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@column/ui';

import { ignoreGlobalNotificationInteraction } from '~/components/NotificationList';
import { useCreateCounterparty, useDeleteCounterparty, useInstitution } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useTransfersFetch } from '~/hooks/useTransfers';
import { Counterparty } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';

import { fieldForApiError } from './_partial/apiError';
import { CounterpartyFields, applyInstitution } from './_partial/sections';
import { FormData } from './_partial/types';

export interface ModalInternationalCounterpartyProps {
  isOpen: boolean;
  onClose: () => void;
  values?: Counterparty;
  callback: (counterparty: Counterparty, currency?: Currency) => void;
}

const DEFAULT_VALUES: Partial<FormData> = {
  routingNumberType: 'aba',
  temporaryData: { institution: null, iban: null, currency: null, oldCounterpartyId: null },
};

/** Starts the form over each time the dialog opens, from `values` when editing. */
const useResetOnOpen = (form: UseFormReturn<FormData>, isOpen: boolean, values?: Counterparty) => {
  const { createRequest: fetchInstitution } = useInstitution();
  const { addDangerNotification } = useNotificationStore();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!values) {
      form.reset(DEFAULT_VALUES);
      return;
    }

    const { id, routingNumber, routingNumberType, accountType, ...rest } = values;
    form.reset({
      ...DEFAULT_VALUES,
      ...Object.fromEntries(
        (
          [
            'accountNumber',
            'address',
            'legalId',
            'legalType',
            'localBankCode',
            'localAccountNumber',
            'localBankCountryCode',
            'localBankName',
            'name',
            'phone',
            'email',
            'description',
          ] as const
        ).map((key) => [key, rest[key] ?? (key === 'address' ? {} : '')])
      ),
      routingNumber: routingNumber ?? '',
      routingNumberType: (routingNumberType as FormData['routingNumberType']) ?? 'aba',
      ...(accountType && { accountType: accountType as FormData['accountType'] }),
      temporaryData: { ...DEFAULT_VALUES.temporaryData!, oldCounterpartyId: id ?? null },
    });

    if (routingNumber) {
      fetchInstitution({ routingNumber })
        .then((institution) => institution && applyInstitution(form, institution))
        .catch((error) => addDangerNotification({ content: error.message }));
    }
  }, [isOpen, values]);
};

export const ModalInternationalCounterparty: FC<ModalInternationalCounterpartyProps> = ({
  isOpen,
  onClose,
  values,
  callback,
}) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { createRequest: createCounterparty } = useCreateCounterparty();
  const { createRequest: fetchTransfers } = useTransfersFetch();
  const { createRequest: deleteCounterparty } = useDeleteCounterparty();
  const { handleSubmitErrors } = useFormErrorHandler();

  const formMethods = useForm<FormData>({ defaultValues: DEFAULT_VALUES, reValidateMode: 'onChange' });
  const { handleSubmit, watch, setError } = formMethods;
  useResetOnOpen(formMethods, isOpen, values);
  const oldCounterpartyId = watch('temporaryData.oldCounterpartyId');

  const onSubmit = ({ temporaryData, ...counterparty }: FormData) => {
    const payload = Object.fromEntries(
      Object.entries(counterparty).filter(([, value]) => value !== '')
    ) as typeof counterparty;

    createCounterparty(payload)
      .then((response) => {
        if (!response) {
          return;
        }

        callback(response, temporaryData.currency ?? undefined);

        if (temporaryData.oldCounterpartyId) {
          addSuccessNotification({ content: 'Counterparty updated successfully' });
          fetchTransfers({ counterpartyId: temporaryData.oldCounterpartyId }).then((transferResponse) => {
            if ((transferResponse?.transfers ?? []).length === 0) {
              deleteCounterparty({ id: temporaryData.oldCounterpartyId! });
            }
          });
        } else {
          addSuccessNotification({ content: 'Counterparty created and selected successfully' });
        }

        onClose();
      })
      .catch((error) => {
        addDangerNotification({ content: error.message });
        const field = fieldForApiError(error);
        if (field) {
          setError(field, { type: 'server', message: error.message });
        }
      });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="flex flex-col gap-0 p-0 [--page-margin:24px] sm:max-w-3xl"
        onInteractOutside={ignoreGlobalNotificationInteraction}
        onOpenAutoFocus={(event) => {
          // The header close button comes first in the DOM; start on the form instead.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector<HTMLInputElement>('form input')?.focus();
        }}
      >
        {/* The overlay scrolls, so the header and footer stick to the viewport while the form scrolls between them. */}
        <DialogHeader className="sticky top-0 z-10 flex-row items-start justify-between gap-4 rounded-t-lg border-b border-gray-100 bg-background px-6 py-4 dark:border-white/[0.08]">
          <DialogTitle>{oldCounterpartyId ? 'Edit Counterparty' : 'Create International Counterparty'}</DialogTitle>
          <DialogDescription className="sr-only">
            The beneficiary’s bank, account and owner details for international wires.
          </DialogDescription>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>
        <FormProvider {...formMethods}>
          <form
            // Fields scrolled into view, e.g. by focus, land clear of the sticky header and footer.
            className="[&_*]:scroll-mt-20 [&_*]:scroll-mb-20"
            onSubmit={(event) => {
              event.stopPropagation();
              handleSubmit(onSubmit, (errors) => handleSubmitErrors<FormData>(errors))(event);
            }}
          >
            <CounterpartyFields />
            <DialogFooter className="sticky bottom-0 z-10 rounded-b-lg border-t border-gray-100 bg-background px-6 py-4 sm:justify-between dark:border-white/[0.08]">
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" size="sm">
                <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                {oldCounterpartyId ? 'Save & Select' : 'Create & Select'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
